// Styled replacements for window.alert / window.confirm. The native ones render as browser
// chrome titled "localhost:3013 says", which is the single most obviously unfinished thing
// in the app. showConfirm is async because a real modal cannot block the way confirm() does;
// Turbo takes an async confirm method directly, and the handful of in-app callers await it.

const STYLE_ID = 'ds-dialog-style'

const CSS = `
.ds-dialog-backdrop {
  position:fixed; inset:0; z-index:10002; display:flex; align-items:center; justify-content:center;
  padding:20px; background:rgba(16,24,40,.45); animation:ds-dialog-fade .15s ease;
}
.ds-dialog {
  width:100%; max-width:420px; background:#fff; border-radius:14px; overflow:hidden;
  box-shadow:0 20px 48px rgba(16,24,40,.24), 0 4px 12px rgba(16,24,40,.08);
  animation:ds-dialog-pop .18s cubic-bezier(.16,1,.3,1);
}
.ds-dialog-body { display:flex; gap:14px; padding:22px 22px 18px; }
.ds-dialog-icon { flex-shrink:0; width:38px; height:38px; border-radius:50%; display:flex; align-items:center; justify-content:center; }
.ds-dialog-icon.is-info   { background:var(--brand-soft, #EEF1F6); color:var(--brand, #1E3A6E); }
.ds-dialog-icon.is-warn   { background:#fef3c7; color:#b45309; }
.ds-dialog-icon.is-danger { background:#fee2e2; color:#dc2626; }
.ds-dialog-text { flex:1; min-width:0; padding-top:2px; }
.ds-dialog-title { font-size:15px; font-weight:700; color:#111827; margin:0 0 4px; }
.ds-dialog-message { font-size:13.5px; line-height:1.5; color:#4b5563; margin:0; white-space:pre-wrap; word-break:break-word; }
.ds-dialog-actions { display:flex; justify-content:flex-end; gap:8px; padding:0 22px 20px; }
.ds-dialog-btn {
  padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; font-family:inherit;
  cursor:pointer; border:1px solid transparent; line-height:1.3;
}
.ds-dialog-btn:focus-visible { outline:2px solid var(--brand, #1E3A6E); outline-offset:2px; }
.ds-dialog-btn.is-cancel  { background:#fff; color:#374151; border-color:#d1d5db; }
.ds-dialog-btn.is-cancel:hover { background:#f9fafb; }
.ds-dialog-btn.is-primary { background:var(--brand, #1E3A6E); color:#fff; }
.ds-dialog-btn.is-primary:hover { background:var(--brand-dark, #152C54); }
.ds-dialog-btn.is-danger  { background:#dc2626; color:#fff; }
.ds-dialog-btn.is-danger:hover { background:#b91c1c; }
@keyframes ds-dialog-fade { from { opacity:0 } to { opacity:1 } }
@keyframes ds-dialog-pop { from { opacity:0; transform:translateY(8px) scale(.98) } to { opacity:1; transform:none } }
@media (prefers-reduced-motion:reduce) {
  .ds-dialog-backdrop, .ds-dialog { animation:none }
}
`

const ICONS = {
  info: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"/><path d="M12 9h.01"/><path d="M11 12h1v4h1"/>',
  warn: '<path d="M12 9v4"/><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"/><path d="M12 16h.01"/>',
  danger: '<path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/>'
}

// Wording is the only signal available for a Turbo confirm, whose message is all we get.
const DESTRUCTIVE_RE = /irreversible|permanently|cannot be (recovered|undone)|delete|remove/i

function ensureStyle () {
  if (document.getElementById(STYLE_ID)) return

  const style = document.createElement('style')

  style.id = STYLE_ID
  style.textContent = CSS
  document.head.appendChild(style)
}

function iconSvg (variant) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true">${ICONS[variant] || ICONS.info}</svg>`
}

function escapeHtml (value) {
  const div = document.createElement('div')

  div.textContent = value == null ? '' : String(value)

  return div.innerHTML
}

// Resolves with `true` only when the confirm button is used; every other exit is a cancel,
// so an accidental Escape or backdrop click can never approve a destructive action.
function openDialog ({ message, title, variant, confirmText, cancelText, withCancel }) {
  ensureStyle()

  return new Promise((resolve) => {
    const previouslyFocused = document.activeElement
    const backdrop = document.createElement('div')

    backdrop.className = 'ds-dialog-backdrop'
    backdrop.innerHTML = `
      <div class="ds-dialog" role="${withCancel ? 'alertdialog' : 'dialog'}" aria-modal="true" aria-label="${escapeHtml(title)}">
        <div class="ds-dialog-body">
          <div class="ds-dialog-icon is-${variant}">${iconSvg(variant)}</div>
          <div class="ds-dialog-text">
            <p class="ds-dialog-title">${escapeHtml(title)}</p>
            <p class="ds-dialog-message">${escapeHtml(message)}</p>
          </div>
        </div>
        <div class="ds-dialog-actions">
          ${withCancel ? `<button type="button" class="ds-dialog-btn is-cancel" data-role="cancel">${escapeHtml(cancelText)}</button>` : ''}
          <button type="button" class="ds-dialog-btn is-${variant === 'danger' ? 'danger' : 'primary'}" data-role="confirm">${escapeHtml(confirmText)}</button>
        </div>
      </div>`

    const finish = (result) => {
      document.removeEventListener('keydown', onKeydown, true)
      backdrop.remove()
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
      resolve(result)
    }

    function onKeydown (event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        finish(false)
      } else if (event.key === 'Enter' && document.activeElement?.dataset?.role !== 'cancel') {
        event.preventDefault()
        finish(true)
      } else if (event.key === 'Tab') {
        // Keep focus inside the dialog: two buttons, so a plain wrap is enough.
        const buttons = [...backdrop.querySelectorAll('button')]
        const next = buttons[(buttons.indexOf(document.activeElement) + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]

        event.preventDefault()
        next?.focus()
      }
    }

    backdrop.querySelector('[data-role="confirm"]').addEventListener('click', () => finish(true))
    backdrop.querySelector('[data-role="cancel"]')?.addEventListener('click', () => finish(false))
    backdrop.addEventListener('mousedown', (event) => { if (event.target === backdrop) finish(false) })
    document.addEventListener('keydown', onKeydown, true)

    document.body.appendChild(backdrop)
    backdrop.querySelector('[data-role="confirm"]').focus()
  })
}

export function showAlert (message, { title = 'Heads up', variant = 'info', confirmText = 'OK' } = {}) {
  return openDialog({ message, title, variant, confirmText, withCancel: false })
}

export function showConfirm (message, options = {}) {
  const danger = options.danger ?? DESTRUCTIVE_RE.test(String(message || ''))

  return openDialog({
    message,
    title: options.title || (danger ? 'Are you sure?' : 'Please confirm'),
    variant: danger ? 'danger' : 'warn',
    confirmText: options.confirmText || (danger ? 'Yes, continue' : 'Confirm'),
    cancelText: options.cancelText || 'Cancel',
    withCancel: true
  })
}
